import { useCallback, useState, memo } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import { normalizeEmails, isValidEmail } from "../../utils/emailValidation";

// ---------------------------------------------------------------------------
// EmailInputField Component
// Reusable email input with validation, editing, and support for single/multiple
// ---------------------------------------------------------------------------
const EmailInputField = memo(function EmailInputField({
  value = [],
  onChange,
  multiple = true,
  label = "Add email address(es)",
  placeholder = "Type email and press Enter or comma",
  size = "small",
  fullWidth = true,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const normalizedValue = normalizeEmails(value);

  const commitEmailInput = useCallback(() => {
    const pending = normalizeEmails(inputValue);
    if (!pending.length) {
      setInputValue("");
      return;
    }

    const validEmails = pending.filter((e) => isValidEmail(e));
    const invalidEmails = pending.filter((e) => !isValidEmail(e));

    if (invalidEmails.length) {
      alert("Invalid email(s): " + invalidEmails.join(", "));
    }

    if (validEmails.length) {
      if (multiple) {
        const merged = [...normalizedValue, ...validEmails];
        const deduplicated = Array.from(new Set(merged));
        onChange(deduplicated);
      } else {
        // For single email mode, take the first valid email
        onChange([validEmails[0]]);
      }
    }
    setInputValue("");
  }, [inputValue, normalizedValue, multiple, onChange]);

  const handleStartEdit = useCallback((index, currentEmail) => {
    setEditingIndex(index);
    setEditingValue(currentEmail);
  }, []);

  const handleSaveEdit = useCallback(() => {
    const newEmail = editingValue.trim();
    if (!newEmail) {
      setEditingIndex(null);
      setEditingValue("");
      return;
    }

    if (!isValidEmail(newEmail)) {
      alert("Invalid email: " + newEmail);
      return;
    }

    const updated = [...normalizedValue];
    updated[editingIndex] = newEmail;
    const deduplicated = Array.from(new Set(updated));
    onChange(deduplicated);
    setEditingIndex(null);
    setEditingValue("");
  }, [editingIndex, editingValue, normalizedValue, onChange]);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingValue("");
  }, []);

  const handleRemoveEmail = useCallback((index) => {
    const updated = normalizedValue.filter((_, i) => i !== index);
    onChange(updated);
  }, [normalizedValue, onChange]);

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo
      size={size}
      fullWidth={fullWidth}
      options={[]}
      value={normalizedValue}
      inputValue={inputValue}
      onInputChange={(_, val) => setInputValue(val || "")}
      onChange={(_, val, reason) => {
        if (reason === "removeOption" || reason === "clear") {
          onChange(normalizeEmails(val));
        }
      }}
      disabled={disabled}
      clearOnBlur={false}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) =>
          editingIndex === index ? (
            <Box
              key={`email_edit_${index}`}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                flex: multiple ? 1 : undefined,
                minWidth: "200px",
                display: "flex",
                gap: 0.5,
              }}
            >
              <TextField
                fullWidth
                size={size}
                variant="outlined"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                autoFocus
                inputProps={{ style: { padding: "8px" } }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveEdit();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    handleCancelEdit();
                  }
                }}
                onBlur={handleSaveEdit}
                disabled={disabled}
              />
            </Box>
          ) : (
            <Chip
              variant="outlined"
              size={size}
              label={option}
              onDoubleClick={() => handleStartEdit(index, option)}
              {...getTagProps({ index })}
            />
          )
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={() => commitEmailInput()}
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              commitEmailInput();
              return;
            }

            if (
              event.key === "Enter" ||
              event.key === "," ||
              event.key === ";"
            ) {
              event.preventDefault();
              commitEmailInput();
            }
          }}
        />
      )}
    />
  );
});

export default EmailInputField;
